import {
  branchesService,
  carrierServicesService,
  carriersService,
  countriesService,
  currenciesService,
  directInterlinesService,
  equipmentTypesService,
  pricingTypesService,
  shipTypesService,
  statesService,
} from '@/modules/entities';

import { type AccessorialsFilters, type AccessorialsFiltersApi, DANGEROUS_GOODS_OPTIONS } from '../../models';

const UNRESOLVED_KEY = 0;

type FormOption<T extends string | number> = { id: T; label: string };

const toFormOption = <T extends string | number>(id: T | null | undefined): FormOption<T> | null => {
  if (id === null || id === undefined) {
    return null;
  }

  return { id, label: '' };
};

const toFormNameOption = (name: string | null | undefined): FormOption<number> | null => {
  if (!name) {
    return null;
  }

  return { id: UNRESOLVED_KEY, label: name };
};

const toFormString = (value: number | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return String(value);
};

const toDangerousGoodsOption = (value: boolean | undefined) => {
  if (value === undefined) {
    return null;
  }

  return DANGEROUS_GOODS_OPTIONS.find((option) => option.id === String(value)) ?? null;
};

// URL params -> form objects
export const normalizeAccessorialsFilters = async (filters: AccessorialsFiltersApi): Promise<AccessorialsFilters> => {
  const [
    branch,
    carrier,
    service,
    equipmentType,
    currency,
    pricingType,
    shipType,
    directInterline,
    originCountry,
    destinationCountry,
    originState,
    destinationState,
  ] = await Promise.all([
    filters.branchKey ? branchesService.find(String(filters.branchKey)) : null,
    filters.carrierKey ? carriersService.find(String(filters.carrierKey)) : null,
    filters.serviceKey ? carrierServicesService.find(String(filters.serviceKey)) : null,
    filters.trailerKey ? equipmentTypesService.find(String(filters.trailerKey)) : null,
    filters.currencyCode ? currenciesService.find(filters.currencyCode) : null,
    filters.pricingType ? pricingTypesService.find(filters.pricingType) : null,
    filters.shipType ? shipTypesService.find(filters.shipType) : null,
    filters.directInterLine ? directInterlinesService.find(filters.directInterLine) : null,
    filters.originCountry ? countriesService.find(filters.originCountry) : null,
    filters.destinationCountry ? countriesService.find(filters.destinationCountry) : null,
    filters.originState && filters.originCountry
      ? statesService.find(filters.originState, filters.originCountry)
      : null,
    filters.destinationState && filters.destinationCountry
      ? statesService.find(filters.destinationState, filters.destinationCountry)
      : null,
  ]);

  return {
    accessorialCode: toFormOption(filters.accessorialCode),
    accessorialKey: toFormString(filters.accessorialKey),
    branch,
    carrier,
    currency,
    destinationCity: filters.destinationCity ?? null,
    destinationCountry,
    destinationLocationKey: toFormOption(filters.destinationLocationKey),
    destinationPortKey: toFormNameOption(filters.destinationPortName),
    destinationPostalFrom: filters.destinationPostalFrom ?? null,
    destinationPostalTo: filters.destinationPostalTo ?? null,
    destinationState,
    directInterline,
    effectiveFromDate: filters.effectiveFromDate,
    effectiveToDate: filters.effectiveToDate,
    endEffectiveToDate: filters.endEffectiveToDate,
    equipmentType,
    isDangerousGoods: toDangerousGoodsOption(filters.dangerousGoods),
    laneKey: toFormOption(filters.laneKey),
    originCity: filters.originCity ?? null,
    originCountry,
    originLocationKey: toFormOption(filters.originLocationKey),
    originPortKey: toFormNameOption(filters.originPortName),
    originPostalFrom: filters.originPostalFrom ?? null,
    originPostalTo: filters.originPostalTo ?? null,
    originState,
    pricingType,
    service,
    shipType,
    startEffectiveFromDate: filters.startEffectiveFromDate,
  };
};

// Form objects -> URL params
export const normalizeAccessorialsFiltersUpdate = (
  filters: AccessorialsFilters,
  customerKey: number,
): AccessorialsFiltersApi => {
  return {
    accessorialCode: filters.accessorialCode?.id ?? undefined,
    accessorialKey: filters.accessorialKey ? Number(filters.accessorialKey) : undefined,
    branchKey: filters.branch ? Number(filters.branch.id) : undefined,
    carrierKey: filters.carrier ? Number(filters.carrier.id) : undefined,
    currencyCode: filters.currency ? String(filters.currency.id) : undefined,
    customerKey,
    dangerousGoods:
      filters.isDangerousGoods?.id === 'true' ? true : filters.isDangerousGoods?.id === 'false' ? false : undefined,
    destinationCity: filters.destinationCity ?? undefined,
    destinationCountry: filters.destinationCountry ? String(filters.destinationCountry.id) : undefined,
    destinationLocationKey: filters.destinationLocationKey?.id ?? undefined,
    destinationPortName: filters.destinationPortKey?.label ?? undefined,
    destinationPostalFrom: filters.destinationPostalFrom ?? undefined,
    destinationPostalTo: filters.destinationPostalTo ?? undefined,
    destinationState: filters.destinationState ? String(filters.destinationState.id) : undefined,
    directInterLine: filters.directInterline ? String(filters.directInterline.id) : undefined,
    effectiveFromDate: filters.effectiveFromDate,
    effectiveToDate: filters.effectiveToDate,
    endEffectiveToDate: filters.endEffectiveToDate || undefined,
    laneKey: filters.laneKey?.id ?? undefined,
    originCity: filters.originCity ?? undefined,
    originCountry: filters.originCountry ? String(filters.originCountry.id) : undefined,
    originLocationKey: filters.originLocationKey?.id ?? undefined,
    originPortName: filters.originPortKey?.label ?? undefined,
    originPostalFrom: filters.originPostalFrom ?? undefined,
    originPostalTo: filters.originPostalTo ?? undefined,
    originState: filters.originState ? String(filters.originState.id) : undefined,
    pricingType: filters.pricingType ? String(filters.pricingType.id) : undefined,
    serviceKey: filters.service ? Number(filters.service.id) : undefined,
    shipType: filters.shipType ? String(filters.shipType.id) : undefined,
    startEffectiveFromDate: filters.startEffectiveFromDate || undefined,
    trailerKey: filters.equipmentType ? Number(filters.equipmentType.id) : undefined,
  };
};
